import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  ChevronRight, ImageIcon, ImagePlus, Pencil, Plus, Save, Trash2, X,
} from 'lucide-react';

import {
  completeCategoryImageUpload, createCurrency, createProductCategory, prepareCategoryImageUpload,
  putProductImage, removeCategoryImage, updateCurrency, updateProductCategory, updateStorefrontCountry,
} from './api';
import { countryName } from './countries';
import { Empty, XomicsSelect } from './parts';
import { Field, errorMessage, fieldAria } from './ui';
import { tx } from './i18n';
import { Switch, slug, uniqueCode } from './ProductOptions';
import { ShopAddressField } from './ProductPageText';
import './coupons.css';

const EMPTY_CATEGORY = {
  name: '', description: '', parentId: '', isActive: true,
};
const byOrder = (left, right) => left.sortOrder - right.sortOrder || left.name.localeCompare(right.name);
const PHOTO_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/avif']);
const MAX_PHOTO_BYTES = 20 * 1024 * 1024;

async function sha256File(file) {
  const digest = await window.crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

// A category's photo, drawn on the store's category tiles, with what changes
// it. Each button acts at once.
function CategoryPhoto({ category, mayManage, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const image = category.image;
  const preview = image?.variants?.card?.url || image?.url || null;

  async function upload(file) {
    if (!file) return;
    if (!PHOTO_TYPES.has(file.type) || file.size < 32 || file.size > MAX_PHOTO_BYTES) {
      setError(tx('ui.products.settings.photo_invalid'));
      return;
    }
    setBusy('upload');
    setError('');
    try {
      const declaration = { contentType: file.type, byteSize: file.size, sha256: await sha256File(file) };
      const prepared = await prepareCategoryImageUpload(category.id, declaration);
      if (!prepared.alreadyUploaded) await putProductImage(prepared.putUrl, file);
      await completeCategoryImageUpload(category.id, {
        ...declaration, objectKey: prepared.objectKey, altText: category.name,
      });
      onChanged();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy('');
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  async function remove() {
    setBusy('remove');
    setError('');
    try {
      await removeCategoryImage(category.id);
      onChanged();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="xo-cg-photo">
      <div className="xo-cg-art">
        {preview ? <img src={preview} alt={image.altText || category.name} /> : <ImageIcon aria-hidden="true" />}
      </div>
      {mayManage && (
        <div className="xo-cg-photo-actions">
          <button type="button" className="xo-btn-2" disabled={busy !== ''} onClick={() => inputRef.current?.click()}>
            <ImagePlus aria-hidden="true" />
            {busy === 'upload' ? tx('ui.products.settings.photo_uploading')
              : image ? tx('ui.products.settings.photo_replace') : tx('ui.products.settings.photo_upload')}
          </button>
          {image && (
            <button type="button" className="xo-btn-2" disabled={busy !== ''} onClick={remove}>
              <Trash2 aria-hidden="true" />{tx('ui.products.settings.photo_remove')}
            </button>
          )}
          <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/avif" hidden
            onChange={(event) => upload(event.target.files?.[0])} />
        </div>
      )}
      {error && <p className="xo-alert t-bad">{error}</p>}
    </div>
  );
}
const EMPTY_CURRENCY = {
  code: '', name: '', minorUnit: '2', isActive: true,
};

function Toggle({ checked, disabled, label, onChange }) {
  return (
    <label className="xo-settings-toggle">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={onChange} />
      <span aria-hidden="true"><i /></span>
      <b>{label}</b>
    </label>
  );
}

// A category's own code, which nobody types: made from its name, and not one
// that is taken.
function codeFor(name, categories) {
  const taken = new Set(categories.map((category) => category.code));
  const base = slug(name);
  return uniqueCode(base.length < 2 ? `category-${base}`.replace(/-+$/, '') : base, taken, { fallback: 'category' });
}

// One category, or a new one (`category` null): its picture, its name and
// words, whether it shows. The catalogue's, so the same for every office.
// Nothing but the picture changes until Save.
function CategoryForm({
  category, categories, mayManage, onClose, onChanged,
}) {
  const fresh = category === null;
  const topLevel = categories.filter((entry) => entry.parentId === null).sort(byOrder);
  const [form, setForm] = useState(() => (fresh ? EMPTY_CATEGORY : {
    name: category.name,
    description: category.description || '',
    isActive: category.isActive,
  }));
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const set = (field, value) => { setTouched(true); setForm((current) => ({ ...current, [field]: value })); };

  const close = useCallback(() => {
    if (busy) return;
    if (touched && !window.confirm(tx('ui.products.detail.discard_changes'))) return;
    onClose();
  }, [busy, onClose, touched]);

  useEffect(() => {
    const closeOnEscape = (event) => { if (event.key === 'Escape') close(); };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [close]);

  async function save(event) {
    event.preventDefault();
    const name = form.name.trim();
    if (name === '') return;
    setBusy(true);
    setError('');
    try {
      const description = form.description.trim() || null;
      if (fresh) {
        const parentId = form.parentId === '' ? null : Number(form.parentId);
        const siblings = categories.filter((entry) => entry.parentId === parentId);
        const sortOrder = siblings.length === 0
          ? (parentId === null ? 100 : 10)
          : Math.max(...siblings.map((entry) => entry.sortOrder)) + 10;
        await createProductCategory({
          code: codeFor(name, categories), name, description, parentId, sortOrder, isActive: form.isActive,
        });
      } else {
        await updateProductCategory(category.id, {
          code: category.code,
          name,
          description,
          parentId: category.parentId,
          sortOrder: category.sortOrder,
          isActive: form.isActive,
        });
      }
      onChanged();
      onClose();
    } catch (caught) {
      setError(errorMessage(caught));
      setBusy(false);
    }
  }

  const disabled = !mayManage || busy;
  const id = fresh ? 'new' : category.id;

  return (
    <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={close}>
      <form className="xo-pc-form is-narrow" role="dialog" aria-modal="true" aria-labelledby="xo-category-form-title"
        onSubmit={save} onMouseDown={(event) => event.stopPropagation()}>
        <header className="xo-pc-form-head">
          <div>
            <h2 id="xo-category-form-title">{fresh ? tx('ui.products.settings.add_category') : category.name}</h2>
          </div>
          <button type="button" className="xo-icon-btn" aria-label={tx("ui.products.close_bbfa773e")} disabled={busy} onClick={close}>
            <X aria-hidden="true" /></button>
        </header>

        <div className="xo-pc-form-body">
          {!fresh && <CategoryPhoto category={category} mayManage={mayManage} onChanged={onChanged} />}
          <div className="xo-pc-part">
            <Field id={`category-name-${id}`} label={tx('ui.products.settings.category_name')}>
              <input {...fieldAria(`category-name-${id}`)} type="text" required value={form.name} disabled={disabled}
                onChange={(event) => set('name', event.target.value)} />
            </Field>
            {fresh && (
              <Field id="category-parent-new" label={tx('ui.products.settings.parent_category')}>
                <XomicsSelect {...fieldAria('category-parent-new')} value={form.parentId} disabled={disabled}
                  onChange={(event) => set('parentId', event.target.value)}>
                  <option value="">{tx('ui.products.settings.top_level')}</option>
                  {topLevel.map((entry) => <option key={entry.id} value={entry.id}>{entry.name}</option>)}
                </XomicsSelect>
              </Field>
            )}
            <Field id={`category-description-${id}`} label={tx('ui.products.settings.description')}>
              <textarea {...fieldAria(`category-description-${id}`)} rows="3" value={form.description} disabled={disabled}
                onChange={(event) => set('description', event.target.value)} />
            </Field>
            {!fresh && category.path && (
              <ShopAddressField id={`category-address-${category.id}`} path={category.path} hint={null} />
            )}
          </div>
          <section className={`xo-pc-switch-row${form.isActive ? ' is-on' : ''}`}>
            <strong>{form.isActive ? tx('ui.values.active') : tx('ui.values.inactive')}</strong>
            <Switch on={form.isActive} disabled={disabled} label={tx('ui.products.settings.active')}
              onChange={(value) => set('isActive', value)} />
          </section>
        </div>

        <footer className="xo-pc-form-foot">
          {error ? <p className="xo-alert t-bad" role="alert">{error}</p> : <span />}
          <button type="button" className="xo-btn-2" disabled={busy} onClick={close}>
            {mayManage ? tx('ui.common.cancel') : tx("ui.products.close_bbfa773e")}</button>
          {mayManage && (
            <button type="submit" className="xo-btn" disabled={busy || form.name.trim() === ''}>
              {busy ? tx('ui.products.settings.saving') : tx('ui.products.settings.save')}
            </button>
          )}
        </footer>
      </form>
    </div>
  );
}

// The catalogue's categories: a top one, then the ones under it. A row opens
// the category; `creating` opens a new one (the page's own button, beside
// its title). `status` keeps every category ('all'), the active ones or the
// inactive ones.
function Categories({
  categories, query, status = 'all', mayManage, onChanged, creating, onCreating: setCreating,
}) {
  const [openId, setOpenId] = useState(null);
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matches = (category) => (status === 'all' || (status === 'active') === category.isActive)
      && (needle === '' || `${category.name} ${category.description || ''}`.toLowerCase().includes(needle));
    return categories.filter((category) => category.parentId === null).sort(byOrder).flatMap((category) => {
      const subs = categories.filter((sub) => sub.parentId === category.id).sort(byOrder).filter(matches);
      // A top category stays above the ones found under it.
      return matches(category) || subs.length > 0 ? [category, ...subs] : [];
    });
  }, [categories, query, status]);
  const open = categories.find((category) => category.id === openId) ?? null;

  return (
    <>
      {rows.length === 0 ? <Empty title={tx('ui.products.settings.no_categories')} /> : (
        <div className="xo-pc-list is-categories">
          <div className="xo-pc-row is-head" aria-hidden="true">
            <span>{tx('ui.products.category_a3c686e7')}</span>
            <span>{tx('ui.products.products_fe0a091f')}</span>
            <span>{tx('ui.store.status')}</span>
            <span />
          </div>
          {rows.map((category) => {
            const picture = category.image?.variants?.thumbnail?.url || category.image?.variants?.card?.url
              || category.image?.url || null;
            return (
              <button type="button" key={category.id} onClick={() => setOpenId(category.id)}
                className={`xo-pc-row${category.isActive ? ' is-on' : ''}${category.parentId === null ? '' : ' is-sub'}`}>
                <span className="xo-pc-name">
                  <span className="xo-pc-thumb">
                    {picture ? <img src={picture} alt="" loading="lazy" decoding="async" /> : <ImageIcon aria-hidden="true" />}
                  </span>
                  <span><strong>{category.name}</strong>{category.description && <small>{category.description}</small>}</span>
                </span>
                <span className="xo-pc-number">{category.productCount}</span>
                <span className="xo-pc-state"><i />{category.isActive ? tx('ui.values.active') : tx('ui.values.inactive')}</span>
                <ChevronRight aria-hidden="true" />
              </button>
            );
          })}
        </div>
      )}
      {open !== null && (
        <CategoryForm key={open.id} category={open} categories={categories} mayManage={mayManage}
          onClose={() => setOpenId(null)} onChanged={onChanged} />
      )}
      {creating && mayManage && (
        <CategoryForm key="new" category={null} categories={categories} mayManage
          onClose={() => setCreating(false)} onChanged={onChanged} />
      )}
    </>
  );
}

function CurrencyRow({ currency, mayManage, onChanged }) {
  const original = {
    name: currency.name,
    minorUnit: String(currency.minorUnit),
    isActive: currency.isActive,
  };
  const [form, setForm] = useState(original);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const dirty = JSON.stringify(form) !== JSON.stringify(original);

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await updateCurrency(currency.code, {
        name: form.name.trim(),
        minorUnit: Number(form.minorUnit),
        isActive: form.isActive,
      });
      setEditing(false);
      onChanged();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  function cancel() {
    setForm(original);
    setError('');
    setEditing(false);
  }

  return (
    <article className={currency.isActive ? 'xo-currency-row' : 'xo-currency-row is-inactive'}>
      <header>
        <strong>{currency.code}</strong>
        <div><b>{currency.name}</b><span>{currency.priceCount} {tx('ui.products.settings.prices')} · {currency.countryCount} {tx('ui.products.settings.countries')}</span></div>
        <span className={currency.isActive ? 'xo-settings-state is-active' : 'xo-settings-state'}>
          <i />{currency.isActive ? tx('ui.values.active') : tx('ui.values.inactive')}
        </span>
        {mayManage && (
          <button type="button" className="xo-settings-edit" onClick={() => setEditing((open) => !open)}>
            <Pencil aria-hidden="true" />{tx('ui.products.settings.edit')}
          </button>
        )}
      </header>
      {editing && (
        <form className="xo-currency-editor" onSubmit={save}>
          <Field id={`currency-name-${currency.code}`} label={tx('ui.products.settings.currency_name')}>
            <input {...fieldAria(`currency-name-${currency.code}`)} required value={form.name}
              disabled={busy} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          </Field>
          <Field id={`currency-decimals-${currency.code}`} label={tx('ui.products.settings.decimals')}>
            <XomicsSelect {...fieldAria(`currency-decimals-${currency.code}`)} value={form.minorUnit}
              disabled={busy} onChange={(event) => setForm({ ...form, minorUnit: event.target.value })}>
              {[0, 1, 2, 3].map((digits) => <option key={digits} value={digits}>{digits}</option>)}
            </XomicsSelect>
          </Field>
          <Toggle checked={form.isActive} disabled={busy} label={tx('ui.products.settings.active')}
            onChange={(event) => setForm({ ...form, isActive: event.target.checked })} />
          <footer>
            <button type="button" className="xo-btn-2" disabled={busy} onClick={cancel}>
              {tx('ui.common.cancel')}
            </button>
            <button type="submit" className="xo-btn" disabled={busy || !dirty || form.name.trim() === ''}>
              <Save aria-hidden="true" />{busy ? tx('ui.products.settings.saving') : tx('ui.products.settings.save')}
            </button>
          </footer>
          {error && <p className="xo-alert t-bad">{error}</p>}
        </form>
      )}
    </article>
  );
}

function Currencies({ currencies, mayManage, onChanged }) {
  const [form, setForm] = useState(EMPTY_CURRENCY);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function create(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await createCurrency({
        ...form,
        code: form.code.toUpperCase(),
        name: form.name.trim(),
        minorUnit: Number(form.minorUnit),
      });
      setForm(EMPTY_CURRENCY);
      setCreating(false);
      onChanged();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="xo-settings-page xo-currency-page">
      <header className="xo-settings-page-head">
        <div>
          <h2>{tx('ui.products.settings.currencies')}</h2>
          <p>{tx('ui.products.settings.currencies_detail')}</p>
        </div>
        <span><strong>{currencies.filter((row) => row.isActive).length}</strong>{tx('ui.values.active')}</span>
        {mayManage && (
          <button type="button" className="xo-btn-2" onClick={() => setCreating((open) => !open)}>
            <Plus aria-hidden="true" />{tx('ui.products.settings.add_currency')}
          </button>
        )}
      </header>

      {creating && (
        <form className="xo-settings-composer xo-currency-composer" onSubmit={create}>
          <Field id="new-currency-code" label={tx('ui.products.settings.currency_code')}>
            <input {...fieldAria('new-currency-code')} required maxLength="3" pattern="[A-Za-z]{3}"
              value={form.code} disabled={busy}
              onChange={(event) => setForm({ ...form, code: event.target.value.toUpperCase() })} />
          </Field>
          <Field id="new-currency-name" label={tx('ui.products.settings.currency_name')}>
            <input {...fieldAria('new-currency-name')} required value={form.name} disabled={busy}
              onChange={(event) => setForm({ ...form, name: event.target.value })} />
          </Field>
          <Field id="new-currency-decimals" label={tx('ui.products.settings.decimals')}>
            <XomicsSelect {...fieldAria('new-currency-decimals')} value={form.minorUnit} disabled={busy}
              onChange={(event) => setForm({ ...form, minorUnit: event.target.value })}>
              {[0, 1, 2, 3].map((digits) => <option key={digits} value={digits}>{digits}</option>)}
            </XomicsSelect>
          </Field>
          <Toggle checked={form.isActive} disabled={busy} label={tx('ui.products.settings.active')}
            onChange={(event) => setForm({ ...form, isActive: event.target.checked })} />
          <footer>
            <button type="button" className="xo-btn-2" disabled={busy}
              onClick={() => { setForm(EMPTY_CURRENCY); setCreating(false); setError(''); }}>
              {tx('ui.common.cancel')}
            </button>
            <button type="submit" className="xo-btn" disabled={busy}>
              <Plus aria-hidden="true" />{tx('ui.products.settings.add_currency')}
            </button>
          </footer>
          {error && <p className="xo-alert t-bad">{error}</p>}
        </form>
      )}

      <div className="xo-currency-list">
        {currencies.map((currency) => (
          <CurrencyRow
            key={`${currency.code}:${currency.name}:${currency.minorUnit}:${currency.isActive}`}
            currency={currency}
            mayManage={mayManage}
            onChanged={onChanged}
          />
        ))}
      </div>
    </section>
  );
}

function blankMarket(currencies, languages) {
  return {
    countryCode: '',
    currencyCode: currencies.find((row) => row.isActive)?.code || '',
    defaultLanguageCode: languages[0]?.code || '',
    isActive: true,
  };
}

function marketForm(country, currencies, languages) {
  if (!country) return blankMarket(currencies, languages);
  return {
    countryCode: country.countryCode,
    currencyCode: country.currencyCode,
    defaultLanguageCode: country.defaultLanguageCode,
    isActive: country.isActive,
  };
}

function Markets({
  countries, currencies, languages, query, mayManage, onChanged,
}) {
  const activeCurrencies = currencies.filter((row) => row.isActive);
  const [form, setForm] = useState(() => marketForm(countries[0], currencies, languages));
  const [selectedCode, setSelectedCode] = useState(countries[0]?.countryCode || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return countries;
    return countries.filter((country) => (
      `${country.countryCode} ${countryName(country.countryCode)} ${country.currencyCode} ${country.languageNativeName || country.languageName}`
        .toLowerCase().includes(needle)
    ));
  }, [countries, query]);

  function choose(country) {
    setSelectedCode(country.countryCode);
    setError('');
    setForm({
      countryCode: country.countryCode,
      currencyCode: country.currencyCode,
      defaultLanguageCode: country.defaultLanguageCode,
      isActive: country.isActive,
    });
  }

  function startNew() {
    setSelectedCode('');
    setError('');
    setForm(blankMarket(currencies, languages));
  }

  async function save(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const code = form.countryCode.toUpperCase();
      await updateStorefrontCountry(code, {
        currencyCode: form.currencyCode,
        defaultLanguageCode: form.defaultLanguageCode,
        isActive: form.isActive,
      });
      setSelectedCode(code);
      onChanged();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="xo-settings-page">
      <header className="xo-settings-page-head">
        <div>
          <h2>{tx('ui.products.coupons.markets')}</h2>
          <p>{tx('ui.products.settings.country_defaults_detail')}</p>
        </div>
        <span><strong>{countries.filter((row) => row.isActive).length}</strong>{tx('ui.values.active')}</span>
        {mayManage && (
          <button type="button" className="xo-btn" onClick={startNew}>
            <Plus aria-hidden="true" />{tx('ui.products.settings.new_market')}
          </button>
        )}
      </header>

      <div className="xo-market-workspace">
        <div className="xo-market-list">
          {shown.length === 0 ? (
            <Empty title={tx('ui.products.settings.country_defaults')} />
          ) : shown.map((country) => (
            <button type="button" key={country.countryCode}
              className={selectedCode === country.countryCode ? 'is-selected' : ''}
              onClick={() => choose(country)}>
              <span className="xo-market-code">{country.countryCode}</span>
              <span className="xo-market-copy">
                <strong>{countryName(country.countryCode)}</strong>
                <small>{country.currencyCode} · {country.languageNativeName || country.languageName}</small>
              </span>
              <span className={country.isActive ? 'xo-settings-state is-active' : 'xo-settings-state'}>
                <i />{country.isActive ? tx('ui.values.active') : tx('ui.values.inactive')}
              </span>
              <ChevronRight aria-hidden="true" />
            </button>
          ))}
        </div>

        <form className="xo-market-editor" onSubmit={save}>
          <header>
            <span>{form.countryCode || '—'}</span>
            <div>
              <strong>{form.countryCode ? countryName(form.countryCode) : tx('ui.products.coupons.markets')}</strong>
              <small>{tx('ui.products.settings.country_defaults')}</small>
            </div>
          </header>
          <Field id="market-country-code" label={tx('ui.locations.country_code')}>
            <input {...fieldAria('market-country-code')} required maxLength="2" pattern="[A-Za-z]{2}"
              value={form.countryCode} disabled={!mayManage || busy || selectedCode !== ''}
              onChange={(event) => setForm({ ...form, countryCode: event.target.value.toUpperCase() })} />
          </Field>
          <Field id="market-currency" label={tx('ui.transactions.currency')}>
            <XomicsSelect {...fieldAria('market-currency')} value={form.currencyCode} disabled={!mayManage || busy}
              onChange={(event) => setForm({ ...form, currencyCode: event.target.value })}>
              {activeCurrencies.map((currency) => (
                <option key={currency.code} value={currency.code}>{currency.code} · {currency.name}</option>
              ))}
            </XomicsSelect>
          </Field>
          <Field id="market-language" label={tx('ui.reports.language_89b86ab0')}>
            <XomicsSelect {...fieldAria('market-language')} value={form.defaultLanguageCode} disabled={!mayManage || busy}
              onChange={(event) => setForm({ ...form, defaultLanguageCode: event.target.value })}>
              {languages.map((language) => (
                <option key={language.code} value={language.code}>
                  {language.nativeName || language.name} · {language.code}
                </option>
              ))}
            </XomicsSelect>
          </Field>
          <Toggle checked={form.isActive} disabled={!mayManage || busy}
            label={tx('ui.products.settings.active')}
            onChange={(event) => setForm({ ...form, isActive: event.target.checked })} />
          {mayManage && (
            <footer>
              {selectedCode !== '' && (
                <button type="button" className="xo-btn-2" disabled={busy} onClick={startNew}>
                  {tx('ui.common.cancel')}
                </button>
              )}
              <button type="submit" className="xo-btn" disabled={busy || form.countryCode.length !== 2
                || form.currencyCode === '' || form.defaultLanguageCode === ''}>
                <Save aria-hidden="true" />{tx('ui.products.settings.save_market')}
              </button>
            </footer>
          )}
          {error && <p className="xo-alert t-bad">{error}</p>}
        </form>
      </div>
    </section>
  );
}

// The catalogue's categories (XomicsCategories.js) have their own page. The
// countries the storefront sells in with their currency and language, the
// currencies themselves and the subscription saving are shown by no page.
export {
  Categories, Currencies, Markets,
};
